import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Button,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { Retailer } from '../types/retailers.types';
import { useDeleteRetailerMutation } from '../hooks/useRetailersQuery';

interface RetailersTableProps {
  retailers: Retailer[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onEdit: (retailerId: string) => void;
  viewMode: 'table' | 'edit';
}

export default function RetailersTable({
  retailers,
  page,
  totalPages,
  onPageChange,
  onEdit,
  viewMode,
}: RetailersTableProps) {
  const deleteMutation = useDeleteRetailerMutation();

  const handleDelete = async (retailerId: string) => {
    if (window.confirm('¿Estás seguro de eliminar este retailer?')) {
      await deleteMutation.mutateAsync(retailerId);
    }
  };

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>País</TableCell>
              <TableCell>ID Retailer</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Estado</TableCell>
              {viewMode === 'edit' && <TableCell align="right">Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {retailers.map((retailer) => (
              <TableRow
                key={retailer.retailerId}
                hover
                sx={{
                  backgroundColor: retailer.isNew
                    ? 'success.50'
                    : retailer.isModified
                    ? 'warning.50'
                    : undefined,
                }}
              >
                <TableCell>{retailer.country}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {retailer.retailerId}
                  </Typography>
                </TableCell>
                <TableCell>{retailer.retailerName}</TableCell>
                <TableCell>
                  <Chip
                    label={retailer.isActive ? 'Activo' : 'Inactivo'}
                    color={retailer.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                {viewMode === 'edit' && (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(retailer.retailerId)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(retailer.retailerId)}
                      color="error"
                      disabled={deleteMutation.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            size="small"
            startIcon={<ChevronLeftIcon />}
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            Anterior
          </Button>
          <Typography variant="body2" color="text.secondary">
            Página {page} de {totalPages}
          </Typography>
          <Button
            size="small"
            endIcon={<ChevronRightIcon />}
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            Siguiente
          </Button>
        </Box>
      )}
    </>
  );
}
